import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Status } from '../../constants';
import { errorFieldsCode, ErrorInfo, invalidDocumentCode } from 'utils/error-codes';
import { RootState } from '../../app/store';
import {
  fetchPaymentInfo,
  fetchDataRequired,
  fetchConfirmPayment,
  fetchShopifyStatus,
  fetchSetCountry,
} from './payment-api';
import { ConfirmPayment, DataRequired, Instalment, Payment, PaymentMethod, SetCountry, ShopifyStatus } from './interfaces';
import { categorizationAndOrderedPMs, getCurrentTime, handleErrorInfo } from '../../utils';
import { HttpError } from 'utils/services/handler';
import Bugsnag from '@bugsnag/browser';

export interface PaymentState {
  loading: boolean;
  error: boolean;
  errorInfo: ErrorInfo | null;
  language: string;
  status: Status;
  deviceId: string | null;
  data: Payment | null;
  countrySelected: boolean;
  paymentMethodSelected: PaymentMethod | null;
  dataRequired: DataRequired;
  confirmPayment: ConfirmPayment;
  shopifyStatus: ShopifyStatus;
  setCountry: SetCountry;
}

const initialState: PaymentState = {
  loading: false,
  error: false,
  errorInfo: null,
  language: 'en',
  status: Status.INIT,
  deviceId: null,
  data: null,
  countrySelected: false,
  paymentMethodSelected: null,
  dataRequired: {
    loading: false,
    error: false,
    errorInfo: null,
    errorFields: null,
    inputs: null,
    instalmentSelected: null,
  },
  confirmPayment: {
    loading: false,
    error: false,
    errorInfo: null,
    noRetryError: false,
    success: null,
    summaryData: null,
  },
  shopifyStatus: {
    loading: false,
    error: false,
    errorInfo: null,
    success: null,
  },
  setCountry: {
    loading: false,
    error: false,
    errorInfo: null,
  },
};

export const getPaymentInfoAsync = createAsyncThunk(
  'payment/get-payment-info',
  async (token: string | undefined, { rejectWithValue }) => {
    const { response, error } = await fetchPaymentInfo(token);
    if (response) {
      return response;
    }
    return rejectWithValue(error);
  }
);

export const getDataRequiredAsync = createAsyncThunk(
  'payment/get-data-required',
  async (paymentMethod: PaymentMethod, { rejectWithValue }) => {
    const { response, error } = await fetchDataRequired(paymentMethod.id);
    if (response) {
      return { inputs: response, paymentMethod };
    }
    return rejectWithValue(error);
  }
);

export const confirmPaymentAsync = createAsyncThunk(
  'payment/confirm-payment',
  async (
    {
      paymentMethod,
      values,
    }: { paymentMethod?: PaymentMethod; values: { [key: string]: string }; extraData?: { bin?: string } },
    { getState, rejectWithValue }
  ) => {
    const confirm = async () => {
      const state = getState() as RootState;
      const { deviceId, data } = state.payment;
      const { response, error } = await fetchConfirmPayment(deviceId || '', data?.country || '', values);
      if (response) {
        return response;
      }
      return rejectWithValue(error);
    };

    if (paymentMethod) {
      const { response, error } = await fetchDataRequired(paymentMethod.id);
      if (response) {
        return confirm();
      }
      return rejectWithValue(error);
    } else {
      return confirm();
    }
  }
);

export const getShopifyStatusAsync = createAsyncThunk(
  'payment/get-shopyfy-status',
  async (token: string, { rejectWithValue }) => {
    const { response, error } = await fetchShopifyStatus(token);
    if (response) {
      return response;
    }
    return rejectWithValue(error);
  }
);

export const setCountryAsync = createAsyncThunk(
  'payment/set-country',
  async (country: string, { rejectWithValue, dispatch }) => {
    const { response, error } = await fetchSetCountry(country);
    if (response) {
      dispatch(getPaymentInfoAsync());
      return response;
    }
    return rejectWithValue(error);
  }
);

export const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    saveDeviceId: (state, action: PayloadAction<string>) => {
      state.deviceId = action.payload;
    },
    setInstalmentSelected: (state, action: PayloadAction<Instalment | null>) => {
      state.dataRequired.instalmentSelected = action.payload;
    },
    clearPaymentSelected: state => {
      state.paymentMethodSelected = null;
      state.dataRequired.inputs = null;
      state.dataRequired.instalmentSelected = null;
    },
    setLoadingConfirm: (state, action: PayloadAction<boolean>) => {
      state.confirmPayment.loading = action.payload;
    },
    cleanDataRequiredError: state => {
      state.dataRequired.error = false;
      state.dataRequired.errorInfo = null;
    },
    cleanConfirmPaymentError: state => {
      state.confirmPayment.error = false;
      state.confirmPayment.errorInfo = null;
    },
  },
  extraReducers: builder => {
    builder
      // GET PAYMENT INFO
      .addCase(getPaymentInfoAsync.pending, state => {
        state.loading = true;
        state.error = false;
        state.errorInfo = null;
        state.data = null;
      })
      .addCase(getPaymentInfoAsync.fulfilled, (state, action) => {
        const { language, type, country, clientInfo, paymentMethods } = action.payload;

        Bugsnag.addMetadata('paymentInfo', {
          type,
          country,
          clientInfo,
        });

        state.loading = false;
        state.error = false;
        state.errorInfo = null;
        state.language = language;
        state.status = Status.SUCCESS;
        state.data = {
          ...action.payload,
          paymentMethodsGrouped: paymentMethods ? categorizationAndOrderedPMs(paymentMethods) : undefined,
        };
        state.countrySelected = !!country;
      })
      .addCase(getPaymentInfoAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.errorInfo = handleErrorInfo(action.payload as HttpError);
        state.status = Status.ERROR;
      })
      // GET DATA REQUIRED
      .addCase(getDataRequiredAsync.pending, state => {
        state.dataRequired.loading = true;
        state.dataRequired.error = false;
        state.dataRequired.errorInfo = null;
        state.dataRequired.inputs = null;
      })
      .addCase(getDataRequiredAsync.fulfilled, (state, action) => {
        const { inputs, paymentMethod } = action.payload;

        state.dataRequired.loading = false;
        state.dataRequired.error = false;
        state.dataRequired.errorInfo = null;
        state.dataRequired.inputs = inputs;
        state.paymentMethodSelected = paymentMethod;
      })
      .addCase(getDataRequiredAsync.rejected, (state, action) => {
        state.dataRequired.loading = false;
        state.dataRequired.error = true;
        state.dataRequired.errorInfo = handleErrorInfo(action.payload as HttpError);
      })
      // CONFIRM
      .addCase(confirmPaymentAsync.pending, state => {
        state.confirmPayment.loading = true;
        state.confirmPayment.error = false;
        state.confirmPayment.errorInfo = null;
        state.confirmPayment.noRetryError = false;
        state.confirmPayment.success = null;
      })
      .addCase(confirmPaymentAsync.fulfilled, (state, action) => {
        const { extraData } = action.meta.arg;
        state.confirmPayment.loading = false;
        state.confirmPayment.error = false;
        state.confirmPayment.errorInfo = null;
        state.confirmPayment.noRetryError = false;
        state.confirmPayment.success = action.payload;
        state.confirmPayment.summaryData = { ...extraData, currentTime: getCurrentTime() } || null;
      })
      .addCase(confirmPaymentAsync.rejected, (state, action) => {
        const { values } = action.meta.arg;
        const errorInfo = handleErrorInfo(action.payload as HttpError);
        const showInside = !!(errorInfo === null || (errorInfo && errorInfo.showInside));
        const errorFields: { [key: string]: string } = {};

        if (errorInfo && errorInfo.code === errorFieldsCode && errorInfo.errorFields) {
          errorInfo.errorFields.forEach(field => {
            errorFields[field] = values[field];
          });
        } else if (errorInfo && errorInfo.code === invalidDocumentCode) {
          errorFields.clientDocument = values.clientDocument;
        }

        if (Object.keys(errorFields).length !== 0) {
          state.dataRequired.errorFields = errorFields;
        } else {
          state.confirmPayment.error = true;
          state.confirmPayment.errorInfo = errorInfo;
          state.confirmPayment.noRetryError = !showInside;
        }
        state.confirmPayment.loading = false;
      })
      // SHOPIFY STATUS
      .addCase(getShopifyStatusAsync.pending, state => {
        state.shopifyStatus.loading = true;
        state.shopifyStatus.error = false;
        state.shopifyStatus.errorInfo = null;
        state.shopifyStatus.success = null;
      })
      .addCase(getShopifyStatusAsync.fulfilled, (state, action) => {
        state.shopifyStatus.loading = false;
        state.shopifyStatus.error = false;
        state.shopifyStatus.errorInfo = null;
        state.shopifyStatus.success = action.payload;
      })
      .addCase(getShopifyStatusAsync.rejected, (state, action) => {
        state.shopifyStatus.loading = false;
        state.shopifyStatus.error = true;
        state.shopifyStatus.errorInfo = handleErrorInfo(action.payload as HttpError);
      })
      // SET COUNTRY
      .addCase(setCountryAsync.pending, state => {
        state.setCountry.loading = true;
        state.setCountry.error = false;
        state.setCountry.errorInfo = null;
      })
      .addCase(setCountryAsync.fulfilled, (state, action) => {
        state.setCountry.loading = false;
        state.setCountry.error = false;
        state.setCountry.errorInfo = null;
      })
      .addCase(setCountryAsync.rejected, (state, action) => {
        state.setCountry.loading = false;
        state.setCountry.error = true;
        state.setCountry.errorInfo = handleErrorInfo(action.payload as HttpError);
      });
  },
});

export const {
  saveDeviceId,
  setInstalmentSelected,
  clearPaymentSelected,
  setLoadingConfirm,
  cleanDataRequiredError,
  cleanConfirmPaymentError,
} = paymentSlice.actions;

export const selectLoading = (state: RootState) => state.payment.loading;
export const selectError = (state: RootState) => state.payment.error;
export const selectErrorInfo = (state: RootState) => state.payment.errorInfo;
export const selectStatus = (state: RootState) => state.payment.status;
export const selectLanguage = (state: RootState) => state.payment.language;
export const selectPaymentData = (state: RootState) => state.payment.data;
export const selectDataRequired = (state: RootState) => state.payment.dataRequired;
export const selectPaymentMethodSelected = (state: RootState) => state.payment.paymentMethodSelected;
export const selectDeviceId = (state: RootState) => state.payment.deviceId;
export const selectConfirmPayment = (state: RootState) => state.payment.confirmPayment;
export const selectShopifyStatus = (state: RootState) => state.payment.shopifyStatus;
export const selectSetCountry = (state: RootState) => state.payment.setCountry;

export default paymentSlice.reducer;
